import React from 'react';
import ExtractionTable from './ExtractionTable';

// Walk a React subtree and concatenate its text content. react-markdown gives
// us already-rendered elements for cells (sometimes wrapped in <strong>,
// <em>, etc.), so we can't read raw markdown — but the visible text is what
// we want to put in the CSV anyway.
const nodeText = (node) => {
  if (node == null || node === false) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join('');
  if (React.isValidElement(node)) return nodeText(node.props?.children);
  return '';
};

const childrenOfType = (children, type) => {
  const result = [];
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === type) result.push(child);
    // react-markdown wraps in fragments sometimes — recurse one level.
    else if (child.type === React.Fragment) {
      result.push(...childrenOfType(child.props.children, type));
    }
  });
  return result;
};

const extractTableData = (children) => {
  let columns = [];
  const rowCells = [];

  childrenOfType(children, 'thead').forEach((thead) => {
    childrenOfType(thead.props.children, 'tr').forEach((tr) => {
      const headers = childrenOfType(tr.props.children, 'th').map((th) =>
        nodeText(th.props.children).trim()
      );
      if (headers.length) columns = headers;
    });
  });

  childrenOfType(children, 'tbody').forEach((tbody) => {
    childrenOfType(tbody.props.children, 'tr').forEach((tr) => {
      const cells = childrenOfType(tr.props.children, 'td').map((td) =>
        nodeText(td.props.children).trim()
      );
      if (cells.length) rowCells.push(cells);
    });
  });

  if (columns.length === 0 || rowCells.length === 0) return null;

  // Dedupe identical column names ("name" twice → "name", "name (2)"). Otherwise
  // ExtractionTable's column dedupe via Set would silently drop one.
  const seen = new Map();
  const uniqueColumns = columns.map((c) => {
    const key = c || '(blank)';
    const count = (seen.get(key) || 0) + 1;
    seen.set(key, count);
    return count === 1 ? key : `${key} (${count})`;
  });

  const rows = rowCells.map((cells) => {
    const obj = {};
    uniqueColumns.forEach((col, i) => {
      obj[col] = cells[i] ?? '';
    });
    return obj;
  });

  return rows;
};

/**
 * Drop-in replacement for the `<table>` element that react-markdown renders.
 * Routes the parsed GFM table through ExtractionTable so it gets a scroll region,
 * a sticky header, a "Showing N of M" count, and CSV export — same UX as our
 * structured-response tables.
 */
const MarkdownTable = ({ children, title, filenameBase }) => {
  const rows = extractTableData(children);
  if (!rows) return null;
  return (
    <ExtractionTable
      data={rows}
      title={title || 'Table'}
      variant="data"
      filenameBase={filenameBase}
    />
  );
};

export default MarkdownTable;

/**
 * Factory: builds the `components` map for a ReactMarkdown call, pre-binding
 * the CSV filename base (typically the active thread title) so a Download as
 * CSV inside a Markdown-rendered table names the file after the conversation.
 */
export const makeMarkdownTableComponents = (filenameBase) => ({
  table: (props) => <MarkdownTable {...props} filenameBase={filenameBase} />,
});
